import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, AlertCircle, Calculator, FileText, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import CostSimulator from '../components/CostSimulator';
import { formatPrice } from '../lib/utils';
import { SEOHead, faqSchema } from '../components/SEOHead';
import { VIEWPORT_ONCE } from '../lib/motion';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const STEPS = [
  {
    icon: FileText,
    title: 'Vous souscrivez à nos services',
    desc: 'Lors de notre rendez-vous, nous établissons un devis et procédons à votre inscription à l\'avance immédiate auprès de l\'Urssaf.'
  },
  {
    icon: Calculator,
    title: 'Vous ne payez que 50%',
    desc: 'Après chaque prestation, vous ne réglez que le reste à charge (50% du coût total). L\'Urssaf prend en charge l\'autre moitié directement.'
  },
  {
    icon: Shield,
    title: 'Le crédit d\'impôt est automatique',
    desc: 'Plus besoin d\'attendre votre déclaration d\'impôts. L\'avance immédiate déduit automatiquement le crédit d\'impôt au moment du paiement.'
  },
];

const EXAMPLES = [
  {
    service: 'Ménage (3h/semaine)',
    priceBefore: 29.99,
    hours: 3,
    freq: 4,
    label: 'Ménage'
  },
  {
    service: 'Jardinage (4h/mois)',
    priceBefore: 49.90,
    hours: 4,
    freq: 1,
    label: 'Jardinage'
  }
];

const FAQ_TAX = [
  { q: 'Qu\'est-ce que l\'avance immédiate du crédit d\'impôt ?', a: 'L\'avance immédiate est un service proposé par l\'Urssaf qui permet de déduire automatiquement le crédit d\'impôt (50%) au moment du paiement de votre prestation. Concrètement, vous ne payez que la moitié du coût réel.' },
  { q: 'Qui peut bénéficier de l\'avance immédiate ?', a: 'Tous les contribuables français peuvent en bénéficier, qu\'ils soient actifs, retraités ou demandeurs d\'emploi. Il suffit de passer par un organisme de services à la personne agréé comme Ptitservices.com.' },
  { q: 'Comment s\'inscrire à l\'avance immédiate ?', a: 'C\'est très simple ! Lors de notre rendez-vous à votre domicile, nous nous occupons de toutes les démarches d\'inscription à l\'avance immédiate auprès de l\'Urssaf. Vous n\'avez rien à faire.' },
  { q: 'Y a-t-il un plafond pour le crédit d\'impôt ?', a: 'Oui, le plafond annuel des dépenses est de 12 000 € par an (soit 6 000 € de crédit d\'impôt maximum), majoré de 1 500 € par enfant à charge ou membre du foyer de plus de 65 ans. Pour le jardinage, le plafond est de 5 000 €/an.' },
  { q: 'Quels services sont éligibles ?', a: 'Tous nos services sont éligibles au crédit d\'impôt : ménage, repassage, vitres, courses, jardinage, petit bricolage, aide aux repas et assistance administrative.' },
  { q: 'Dois-je encore faire quelque chose sur ma déclaration d\'impôts ?', a: 'Avec l\'avance immédiate, le crédit d\'impôt est déjà déduit. Vous n\'avez qu\'à vérifier les montants pré-remplis sur votre déclaration. Nous vous fournissons une attestation fiscale annuelle.' },
];

export default function CreditImpotPage() {
  return (
    <div>
      <SEOHead
        title="Crédit d'impôt 50 % et avance immédiate"
        description="Tout savoir sur le crédit d'impôt 50% pour les services à la personne et l'avance immédiate Urssaf. Ne payez que le reste à charge : ménage dès 14,99€/h, jardinage dès 24,95€/h. Explication, exemples et simulateur."
        path="/credit-impot"
        schema={faqSchema(FAQ_TAX)}
      />
      {/* Hero */}
      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <span className="text-foreground font-medium">Crédit d'impôt & Avance immédiate</span>
          </div>
          <div className="max-w-3xl">
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Avance immédiate</Badge>
            <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Crédit d'impôt 50% &mdash; ne payez que le reste à charge</h1>
            <p className="text-muted-foreground leading-relaxed text-base sm:text-lg">
              Grâce à l'avance immédiate du crédit d'impôt, vous bénéficiez de 50% de réduction directement sur votre facture. Plus besoin d'attendre votre déclaration d'impôts !
            </p>
          </div>
        </div>
      </section>

      {/* 3 Steps */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Comment ça fonctionne ?</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">L'avance immédiate en 3 étapes simples</p>
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {STEPS.map((step, i) => (
              <motion.div key={`item-${i}`} variants={fadeUp}>
                <Card className="p-6 h-full bg-card border border-border shadow-sm text-center">
                  <div className="w-14 h-14 rounded-xl bg-[hsl(var(--brand-mint))] flex items-center justify-center mx-auto mb-4">
                    <step.icon className="h-7 w-7 text-[hsl(var(--primary))]" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center mx-auto mb-3 font-bold text-sm">{i + 1}</div>
                  <h3 className="font-['Space_Grotesk'] font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Example table */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Exemples concrets</h2>
            <p className="text-muted-foreground">Combien coûte réellement nos services avec l'avance immédiate ?</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {EXAMPLES.map((ex, i) => {
              const total = ex.priceBefore * ex.hours * ex.freq;
              const credit = total * 0.5;
              const after = total - credit;
              return (
                <motion.div key={`item-${i}`} initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
                  <Card className="p-6 bg-card border border-border shadow-sm">
                    <h3 className="font-['Space_Grotesk'] font-semibold mb-4">{ex.service}</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Tarif horaire</span>
                        <span className="font-medium">{formatPrice(ex.priceBefore)}/h</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Heures/mois</span>
                        <span className="font-medium">{ex.hours * ex.freq}h</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Coût total/mois</span>
                        <span className="font-medium">{formatPrice(total)}</span>
                      </div>
                      <div className="flex justify-between items-center text-green-600">
                        <span className="text-sm">Crédit d'impôt 50%</span>
                        <span className="font-semibold">- {formatPrice(credit)}</span>
                      </div>
                      <div className="border-t border-border pt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold">Vous payez (avance immédiate)</span>
                          <span className="text-xl font-bold text-[hsl(var(--primary))]">{formatPrice(after)}/mois</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Soit {formatPrice(ex.priceBefore / 2)}/h après avance immédiate</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 p-4 rounded-lg bg-[hsl(var(--brand-mint))]/30 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              <strong>Important :</strong> Ces exemples sont donnés à titre indicatif. Le montant exact dépend de vos besoins spécifiques. Contactez-nous pour un devis personnalisé.
            </p>
          </div>
        </div>
      </section>

      {/* Simulator */}
      <section className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Simulez votre coût</h2>
            <p className="text-sm text-muted-foreground">Utilisez notre simulateur pour estimer votre reste à charge</p>
          </div>
          <CostSimulator />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Questions fréquentes</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ_TAX.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} className="border border-border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Profitez de l'avance immédiate dès maintenant</h2>
          <p className="text-primary-foreground/80 mb-8">Nous nous occupons de toutes les démarches d'inscription. Demandez votre devis gratuit.</p>
          <Link to="/devis">
            <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg">
              Demander un devis gratuit
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
